import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PmsStatusBadge } from '@/components/pms/PmsStatusBadge';
import { PmsToolbar } from '@/components/pms/PmsToolbar';
import { Sparkles, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';

export
function RoomBoardTab({ hotelId }) {
  const [loading, setLoading] = useState(true);
  const [roomBoard, setRoomBoard] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [statusFilter, setStatusFilter] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [updating, setUpdating] = useState(null);

  useEffect(() => {
    loadRoomBoard();
  }, [hotelId]);

  const loadRoomBoard = async () => {
    try {
      setLoading(true);
      const backendUrl = BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/pms/${hotelId}/room-status`);
      const data = await response.json();
      if (data.success) {
        setRoomBoard(data);
      }
    } catch (error) {
      console.error('Failed to load room board:', error);
      toast.error('Failed to load room board');
    } finally {
      setLoading(false);
    }
  };

  const updateRoomStatus = async (roomId, newStatus) => {
    try {
      setUpdating(roomId);
      const backendUrl = BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/pms/room-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          room_id: roomId,
          new_status: newStatus,
          updated_by: 'owner_dashboard'
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`Room status updated to ${newStatus}`);
        loadRoomBoard();
      } else {
        toast.error(data.error || 'Failed to update room status');
      }
    } catch (error) {
      console.error('Error updating room status:', error);
      toast.error('Failed to update room status');
    } finally {
      setUpdating(null);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <Skeleton className="h-12 w-12 rounded-full mx-auto mb-4" />
        <Skeleton className="h-4 w-48 mx-auto" />
      </div>
    );
  }

  if (!roomBoard) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <p className="text-muted-foreground">Unable to load room board</p>
        </CardContent>
      </Card>
    );
  }

  let filteredRooms = roomBoard.all_rooms || [];
  if (statusFilter) {
    filteredRooms = filteredRooms.filter(r => r.status === statusFilter);
  }
  if (searchQuery) {
    filteredRooms = filteredRooms.filter(r => 
      r.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.room_number?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }

  return (
    <div className="space-y-6" data-testid="room-board-tab">
      <div>
        <h2 className="text-2xl font-bold heading-font mb-2">Room Status Board</h2>
        <p className="text-muted-foreground">View and manage all room statuses in real-time</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold">{roomBoard.total_rooms}</p>
            <p className="text-xs text-muted-foreground">Total Rooms</p>
          </CardContent>
        </Card>
        <Card className="border-[hsl(var(--status-clean)/0.3)] bg-[hsl(var(--status-clean)/0.05)]">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-[hsl(var(--status-clean))]">{roomBoard.summary.clean}</p>
            <p className="text-xs text-muted-foreground">Clean</p>
          </CardContent>
        </Card>
        <Card className="border-[hsl(var(--status-dirty)/0.3)] bg-[hsl(var(--status-dirty)/0.05)]">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-[hsl(var(--status-dirty))]">{roomBoard.summary.dirty}</p>
            <p className="text-xs text-muted-foreground">Dirty</p>
          </CardContent>
        </Card>
        <Card className="border-[hsl(var(--status-occupied)/0.3)] bg-[hsl(var(--status-occupied)/0.05)]">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-[hsl(var(--status-occupied))]">{roomBoard.summary.occupied}</p>
            <p className="text-xs text-muted-foreground">Occupied</p>
          </CardContent>
        </Card>
        <Card className="border-[hsl(var(--status-maintenance)/0.3)] bg-[hsl(var(--status-maintenance)/0.05)]">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-[hsl(var(--status-maintenance))]">{roomBoard.summary.maintenance}</p>
            <p className="text-xs text-muted-foreground">Maintenance</p>
          </CardContent>
        </Card>
      </div>

      <PmsToolbar
        searchPlaceholder="Search rooms..."
        onSearchChange={setSearchQuery}
        showStatusFilter={true}
        onStatusFilterChange={setStatusFilter}
        showViewMode={true}
        onViewModeChange={setViewMode}
        defaultViewMode={viewMode}
      />

      {viewMode === 'grid' ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredRooms.map(room => (
            <Card key={room.id} className="hover:shadow-md transition-shadow" data-testid={`room-card-${room.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{room.room_number || room.name}</CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">{room.type}</p>
                  </div>
                  <PmsStatusBadge status={room.status} size="sm" />
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {room.current_booking_id && (
                  <div className="text-xs bg-primary/5 p-2 rounded">
                    <p className="font-medium">Guest: {room.guest_name || 'N/A'}</p>
                    <p className="text-muted-foreground">Until: {room.checkout_date || 'N/A'}</p>
                  </div>
                )}
                
                <div className="flex flex-wrap gap-1">
                  {room.status !== 'clean' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateRoomStatus(room.id, 'clean')}
                      disabled={updating === room.id}
                      data-testid={`mark-clean-${room.id}`}
                      className="text-xs flex-1"
                    >
                      <Sparkles className="w-3 h-3 mr-1" />
                      Clean
                    </Button>
                  )}
                  {room.status !== 'dirty' && !room.current_booking_id && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateRoomStatus(room.id, 'dirty')}
                      disabled={updating === room.id}
                      data-testid={`mark-dirty-${room.id}`}
                      className="text-xs flex-1"
                    >
                      Dirty
                    </Button>
                  )}
                  {room.status !== 'maintenance' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateRoomStatus(room.id, 'maintenance')}
                      disabled={updating === room.id}
                      data-testid={`mark-maintenance-${room.id}`}
                      className="text-xs flex-1"
                    >
                      Maint.
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Room</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Guest</TableHead>
                <TableHead>Checkout</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRooms.map(room => (
                <TableRow key={room.id} data-testid={`room-row-${room.id}`}>
                  <TableCell className="font-medium">{room.room_number || room.name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{room.type}</TableCell>
                  <TableCell><PmsStatusBadge status={room.status} size="sm" /></TableCell>
                  <TableCell className="text-sm">{room.guest_name || '—'}</TableCell>
                  <TableCell className="text-sm">{room.checkout_date || '—'}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {room.status !== 'clean' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => updateRoomStatus(room.id, 'clean')}
                          disabled={updating === room.id}
                        >
                          <Sparkles className="w-4 h-4" />
                        </Button>
                      )}
                      {room.status !== 'dirty' && !room.current_booking_id && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => updateRoomStatus(room.id, 'dirty')}
                          disabled={updating === room.id}
                        >
                          <ClipboardList className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
